import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Action} from '@ngrx/store';
import {Observable} from 'rxjs';
import {catchError, mergeMap, tap} from 'rxjs/operators';
import {UserApiService} from '../../api/user-api.service';
import {AuthenticationService} from '../../../auth/auth.service';
import {createCallbackActions, emitErrorActions} from '../store.utils';
import {
  CreateNewUserAction,
  CreateNewUserSuccessAction,
  DeleteUserAction,
  GetAllUsersAction,
  GetAllUsersSuccessAction,
  SendUserEmailAction,
  SendUserEmailSuccessAction,
  UpdateUserAction,
  UpdateUserProfileAction,
  UpdateUserProfileSuccessAction,
  UsersActionType,
} from './users.action';

@Injectable()
export class UsersEffects {
  public getAll$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<GetAllUsersAction>(UsersActionType.GET_ALL),
      mergeMap(action => {
        const {onSuccess, onFailure} = action.payload;

        return this.userApiService.getAll().pipe(
          mergeMap(users => [new GetAllUsersSuccessAction({users}), ...createCallbackActions(onSuccess, users)]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public createNew$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<CreateNewUserAction>(UsersActionType.CREATE_NEW),
      mergeMap(action => {
        const {user, onSuccess, onFailure} = action.payload;

        return this.userApiService.createNewUser(user).pipe(
          mergeMap(user => [new CreateNewUserSuccessAction(), ...createCallbackActions(onSuccess, user)]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public update$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateUserAction>(UsersActionType.UPDATE),
      mergeMap(action => {
        const {user, onSuccess, onFailure} = action.payload;

        return this.userApiService.updateUser(user).pipe(
          mergeMap(user => [new CreateNewUserSuccessAction(), ...createCallbackActions(onSuccess, user)]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public delete$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteUserAction>(UsersActionType.DELETE),
      mergeMap(action => {
        const {userId, onSuccess, onFailure} = action.payload;

        return this.userApiService.deleteUser(userId).pipe(
          mergeMap(user => [new CreateNewUserSuccessAction(), ...createCallbackActions(onSuccess, user)]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public updateUserProfile$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateUserProfileAction>(UsersActionType.UPDATE_USER_PROFILE),
      mergeMap(action => {
        const {user, onSuccess, onFailure} = action.payload;

        return this.userApiService.updateUserProfile(user).pipe(
          tap(() => {
            this.authService.setLocalStorageUser(user);
          }),
          mergeMap(msg => [new UpdateUserProfileSuccessAction(msg), ...createCallbackActions(onSuccess, user)]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  public sendUserEmail$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<SendUserEmailAction>(UsersActionType.SEND_USER_EMAIL),
      mergeMap(action => {
        const {userId, timestamp, activeUserId, activeUserName, url, onSuccess, onFailure} = action.payload;

        return this.userApiService.sendUserEmail(userId, timestamp, activeUserId, activeUserName, url).pipe(
          mergeMap(user => [new SendUserEmailSuccessAction({user}), ...createCallbackActions(onSuccess, user)]),
          catchError(error => emitErrorActions(error, onFailure))
        );
      })
    )
  );

  constructor(
    private actions$: Actions,
    private userApiService: UserApiService,
    private authService: AuthenticationService
  ) {}
}
